import { supabase } from './supabaseClient';
import { getNegocioId } from './negocioActual';
import { obtenerFotoPrincipal } from '../utils/categorias';
import { combinarEventos } from '../utils/eventosCatalogo';

// Módulo "Anuncios de Meta" (sql/097). Lee la copia de Meta que guarda el
// servidor (api/cuenta?accion=meta-sync) y la cruza con las ventas de Kontrol.
// Origen de cada número: 'meta' (lo reporta Meta), 'kontrol' (tus ventas),
// 'estimado' (cálculo aproximado) o 'pendiente' (falta el dato).

const ZONA = 'America/Tegucigalpa';
export const hoyLocal = () => new Intl.DateTimeFormat('en-CA', { timeZone: ZONA }).format(new Date());
export const sumarDias = (ymd, n) => {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const fechaDe = (iso) => (iso ? new Intl.DateTimeFormat('en-CA', { timeZone: ZONA }).format(new Date(iso)) : null);

// Periodos iguales a los de Meta (así el alcance de personas únicas coincide).
export const PERIODOS = [
  { clave: 'hoy', etiqueta: 'Hoy' },
  { clave: '7d', etiqueta: 'Últimos 7 días', resumen: 'last_7d' },
  { clave: '30d', etiqueta: 'Últimos 30 días', resumen: 'last_30d' },
  { clave: 'mes', etiqueta: 'Este mes', resumen: 'this_month' },
  { clave: 'mespasado', etiqueta: 'Mes pasado', resumen: 'last_month' },
  { clave: 'todo', etiqueta: 'Desde julio', resumen: 'historial' },
];

export function rangoDe(clave, desdeCuenta = '2026-07-01') {
  const hoy = hoyLocal();
  const primeroMes = `${hoy.slice(0, 8)}01`;
  switch (clave) {
    case 'hoy': return { desde: hoy, hasta: hoy };
    case '7d': return { desde: sumarDias(hoy, -7), hasta: sumarDias(hoy, -1) };
    case '30d': return { desde: sumarDias(hoy, -30), hasta: sumarDias(hoy, -1) };
    case 'mes': return { desde: primeroMes, hasta: hoy };
    case 'mespasado': {
      const fin = sumarDias(primeroMes, -1);
      return { desde: `${fin.slice(0, 8)}01`, hasta: fin };
    }
    default: return { desde: String(desdeCuenta).slice(0, 10), hasta: hoy };
  }
}

// ---------- Carga ----------
export async function cargarTodo() {
  const neg = getNegocioId();
  const [cuenta, campanas, conjuntos, anuncios, insights, resumen, pixel, cambios, referencias] = await Promise.all([
    supabase.from('meta_cuentas').select('*').eq('negocio_id', neg).maybeSingle(),
    supabase.from('meta_campanas').select('*').eq('negocio_id', neg),
    supabase.from('meta_conjuntos').select('*').eq('negocio_id', neg),
    supabase.from('meta_anuncios').select('*').eq('negocio_id', neg),
    supabase.from('meta_insights').select('anuncio_id,fecha,campana_id,conjunto_id,gasto,impresiones,alcance,clics,clics_enlace,vistas_pagina,mensajes,compras,reproducciones,interacciones').eq('negocio_id', neg).order('fecha').limit(20000),
    supabase.from('meta_resumen').select('*').eq('negocio_id', neg),
    supabase.from('meta_pixel_diario').select('*').eq('negocio_id', neg).order('fecha'),
    supabase.from('meta_cambios').select('*').eq('negocio_id', neg).order('fecha', { ascending: false }).limit(150),
    supabase.from('meta_referencias').select('*').eq('negocio_id', neg).order('revisado_en', { ascending: false }),
  ]);
  const err = [cuenta, campanas, conjuntos, anuncios, insights, resumen, pixel, cambios, referencias].find((r) => r.error);
  if (err) throw err.error;
  if (!cuenta.data) return null;

  const desde = String(cuenta.data.desde).slice(0, 10);
  const { data: ventas, error: ev } = await supabase
    .from('ventas')
    .select('id,ticket_id,producto_id,nombre_producto,cantidad,total,costo_unitario,canal,por_anuncio,vendido_en')
    .eq('negocio_id', neg)
    .gte('vendido_en', `${desde}T00:00:00-06:00`)
    .limit(20000);
  if (ev) throw ev;

  // Pedidos del catálogo con su origen (sql/104).
  const { data: pedidos } = await supabase
    .from('pedidos')
    .select('id,grupo,origen,estado,precio,cantidad,creado_en')
    .eq('negocio_id', neg)
    .gte('creado_en', `${desde}T00:00:00-06:00`)
    .limit(5000);

  // Stock de los productos que salen en los anuncios (enlaces ?producto=).
  const ids = [...new Set((anuncios.data || []).flatMap((a) => a.productos_ids || []))];
  let productos = [];
  if (ids.length) {
    const { data } = await supabase.from('productos').select('id,nombre,codigo,stock,color,talla').in('id', ids);
    productos = data || [];
  }

  // Anuncios de catálogo sin portada (Meta arma las fotos): se muestran 4 prendas con stock.
  let fotosCatalogo = [];
  if ((anuncios.data || []).some((a) => a.formato === 'catalogo' && !a.miniatura_url)) {
    const { data } = await supabase.from('productos').select('foto_principal,foto_1_url,foto_2_url,foto_3_url,foto_4_url,foto_5_url')
      .eq('negocio_id', neg).eq('activo', true).gt('stock', 0).not('foto_1_url', 'is', null)
      .order('creado_en', { ascending: false }).limit(4);
    fotosCatalogo = [...new Set((data || []).map(obtenerFotoPrincipal).filter(Boolean))];
  }

  // Eventos del catálogo que cuenta Kontrol (sql/126): mandan sobre los del píxel.
  const { data: kontrolEv } = await supabase.from('catalogo_eventos_diario').select('fecha,evento,cantidad')
    .eq('negocio_id', neg).gte('fecha', desde).order('fecha').limit(5000);
  const eventos = combinarEventos(pixel.data || [], kontrolEv || [], pedidos || []);

  return {
    cuenta: cuenta.data, campanas: campanas.data || [], conjuntos: conjuntos.data || [], anuncios: anuncios.data || [],
    insights: insights.data || [], resumen: resumen.data || [], pixel: eventos.filas, inicioKontrol: eventos.inicioKontrol, cambios: cambios.data || [],
    referencias: referencias.data || [], ventas: ventas || [], productos, pedidos: pedidos || [], fotosCatalogo,
  };
}

// ¿Este negocio tiene Meta conectado? (solo la dueña lo puede leer)
export async function tieneMetaConectado() {
  const { data } = await supabase.from('meta_cuentas').select('negocio_id').eq('negocio_id', getNegocioId()).maybeSingle();
  return !!data;
}

// Pide al servidor traer los datos nuevos de Meta. `token` es opcional (si aún
// no está el token permanente): se usa una sola vez y no se guarda.
export async function actualizarDesdeMeta(token) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Tu sesión expiró. Vuelve a entrar.');
  const r = await fetch('/api/cuenta?accion=meta-sync', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify(token ? { token } : {}),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok || d.error) {
    const e = new Error(d.mensaje || 'No se pudo actualizar. Intenta de nuevo.');
    e.codigo = d.error;
    throw e;
  }
  return d;
}

// ---------- Cálculos ----------
const n = (v) => Number(v || 0);
export const div = (a, b) => (b > 0 ? a / b : null);

export function sumar(filas) {
  const t = { gasto: 0, impresiones: 0, clics: 0, clics_enlace: 0, vistas_pagina: 0, mensajes: 0, compras: 0, reproducciones: 0, interacciones: 0 };
  for (const f of filas) for (const k of Object.keys(t)) t[k] += n(f[k]);
  t.ctr = div(t.clics_enlace * 100, t.impresiones);
  t.cpc = div(t.gasto, t.clics_enlace);
  t.cpm = div(t.gasto * 1000, t.impresiones);
  t.costoMensaje = div(t.gasto, t.mensajes);
  t.costoCompra = div(t.gasto, t.compras);
  return t;
}

// Qué cuenta como "resultado" según el objetivo de la campaña.
export function tipoResultado(campana) {
  const o = campana?.objetivo || '';
  if (o.includes('TRAFFIC')) return { clave: 'clics_enlace', etiqueta: 'clics' };
  if (o.includes('SALES')) return { clave: 'compras', etiqueta: 'compras' };
  return { clave: 'mensajes', etiqueta: 'mensajes' };
}

// Estado entendible de un anuncio (activo de verdad, pausado, terminado…).
export function estadoAnuncio(anuncio, conjunto, campana) {
  const ef = anuncio.estado_efectivo || '';
  const fin = conjunto?.fin || campana?.fin;
  if (['DISAPPROVED', 'WITH_ISSUES'].includes(ef)) return { clave: 'problema', etiqueta: 'Con problemas', color: '#B91C1C' };
  if (['PENDING_REVIEW', 'IN_PROCESS'].includes(ef)) return { clave: 'revision', etiqueta: 'En revisión', color: '#B45309' };
  if (ef === 'ACTIVE' && fin && new Date(fin) < new Date()) return { clave: 'terminado', etiqueta: 'Terminado', color: '#6B7280' };
  if (ef === 'ACTIVE') return { clave: 'activo', etiqueta: 'Activo', color: '#047857' };
  if (ef === 'ARCHIVED') return { clave: 'terminado', etiqueta: 'Archivado', color: '#6B7280' };
  if (fin && new Date(fin) < new Date()) return { clave: 'terminado', etiqueta: 'Terminado', color: '#6B7280' };
  return { clave: 'pausado', etiqueta: 'Pausado', color: '#9CA3AF' };
}

// Tema del anuncio (para el filtro "producto"): por palabras del nombre y el texto.
const TEMAS = [
  ['Brasieres', /brasier|bras\b|bra\b|sostén|strapless|estraple|varilla|bralette/i],
  ['Trajes de baño', /traje|baño|bikini|playa/i],
  ['Fajas y talladoras', /faja|tallador/i],
  ['Blusas y ropa de dama', /blusa|dama|vestido|short|morazánica|morazanica/i],
  ['Carteras y zapatos', /cartera|zapato|bolso|sandalia|tenis/i],
  ['Belleza y lociones', /belleza|loci|splash|crema|perfume|maquillaje/i],
];
export function temaDe(anuncio) {
  const txt = `${anuncio.nombre || ''} ${anuncio.titulo || ''} ${(anuncio.texto || '').slice(0, 200)}`;
  const t = TEMAS.find(([, re]) => re.test(txt));
  return t ? t[0] : 'Otros';
}
export const TEMAS_LISTA = [...TEMAS.map((t) => t[0]), 'Otros'];

// Ventas de Kontrol agrupadas por ticket (una venta = un ticket).
export function ticketsDe(ventas) {
  const m = new Map();
  for (const v of ventas) {
    const k = v.ticket_id || v.id;
    const t = m.get(k) || { id: k, fecha: fechaDe(v.vendido_en), canal: v.canal, porAnuncio: false, total: 0, ganancia: 0, items: [] };
    t.total += n(v.total);
    t.ganancia += n(v.total) - n(v.costo_unitario) * n(v.cantidad);
    t.porAnuncio = t.porAnuncio || !!v.por_anuncio;
    t.items.push(v);
    m.set(k, t);
  }
  return [...m.values()];
}
export const enLinea = (t) => ['Instagram', 'WhatsApp'].includes(t.canal);

// Cuánto puedes pagar por venta: ganancia promedio de una venta en línea.
// "Equilibrio" = no ganas ni pierdes; "meta" = te quedas con el 70% de la ganancia.
export function costoMaximoPorVenta(tickets) {
  const t = tickets.filter(enLinea);
  if (t.length < 5) return null;
  const ganancia = t.reduce((s, x) => s + x.ganancia, 0) / t.length;
  const ticket = t.reduce((s, x) => s + x.total, 0) / t.length;
  return { ticketPromedio: ticket, gananciaPromedio: ganancia, equilibrio: ganancia, meta: ganancia * 0.3, muestras: t.length };
}

export const dentro = (fecha, r) => fecha >= r.desde && fecha <= r.hasta;

// Días entre dos fechas (inclusive) para las gráficas.
export function diasDe(r) {
  const out = [];
  for (let d = r.desde; d <= r.hasta && out.length < 400; d = sumarDias(d, 1)) out.push(d);
  return out;
}
